import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import FloatingActionButton from "./components/ui/FloatingActionButton";
import AiAssistantChatbot from "./components/ui/AiAssistantChatbot";
import SEO from "./components/seo";
import NotFound from "pages/NotFound";
import PropertySearchBrowse from './pages/property-search-browse';
import Login from './pages/login';
import Home from './pages/home';
import Hostels from './pages/hostels';
import Hotels from './pages/hotels';
import UserDashboard from './pages/user-dashboard';
import PropertyDetail from './pages/property-detail';
import AgentProfile from './pages/agent-profile';
import HostelDetail from './pages/hostels/components/HostelDetail';
import HotelDetail from './pages/hotels/components/HotelDetail';
import Register from './pages/register';
import SavedPropertiesPage from './pages/saved-properties'; 
import AboutUs from './pages/about';
import OurTeam from './pages/team';
import Careers from './pages/careers';
import Press from './pages/press';
import ListProperty from './pages/list-property';
import HelpCenter from './pages/help';
import HowFeesWork from './pages/how-fees-work';
import ContactUs from './pages/contact';
import PrivacyPolicy from './pages/privacy';
import TermsOfService from './pages/terms';
import PropertyGuides from './pages/guides';
import MarketReports from './pages/reports';
import InvestmentTips from './pages/tips';
import Blog from './pages/blog';
import UserProfile from './pages/user-profile'; // Import the new UserProfile page
import VerificationPage from './pages/verification';
import NoticeBoardPage from './pages/notice-board';
import NoticeBoardDetail from './pages/notice-board/detail';
import ProLogin from './pages/pro/login';
import ProRegister from './pages/pro/register';
import WorkspaceHome from './pages/workspace';
import CoBrokerPage from './pages/workspace/co-broker';
import CrmPage from './pages/workspace/crm';
import BookingsPage from './pages/workspace/bookings';
import DocumentsPage from './pages/workspace/documents';
import AgentDashboard from './pages/agent-dashboard';
import AgentProperties from './pages/agent-dashboard/properties';
import AgentPropertyNew from './pages/agent-dashboard/property-new';
import AgentSubscription from './pages/agent-dashboard/subscription';
import AgentPromote from './pages/agent-dashboard/promote';
import NotificationsPage from './pages/notifications';
import MarketPage from './pages/market';
import HtmlSitemap from './pages/sitemap-html';
import { organizationSchema, websiteSchema } from './lib/seo';

const baseUrl = 'https://hirebroker.co';

const seoConfig = {
  '/': {
    title: 'Verified Property Listings in Uganda',
    description: 'Eliminate agent fraud. HireBroker offers verified property listings, land plots, and rentals across Kampala, Wakiso, and Entebbe. For rental hunting, HireBroker holds your brokerage fee until you accept a property.',
    keywords: 'property for sale Uganda, houses for rent Kampala, apartments Kampala, land for sale Wakiso, Entebbe rentals, verified real estate agents Uganda, HireBroker',
    canonicalUrl: `${baseUrl}/`,
    schema: { '@context': 'https://schema.org', '@graph': [organizationSchema(), websiteSchema()] },
  },
  '/hostels': {
    title: 'Hostels for Rent in Uganda & East Africa',
    description: 'Find verified hostels and student accommodation across East Africa. Search available rooms, compare options, and book the best hostel today.',
    canonicalUrl: `${baseUrl}/hostels`
  },
  '/hotels': {
    title: 'Hotels & Hospitality Listings',
    description: 'Discover hotel rooms, safaris, staycations, and hospitality properties across Uganda and East Africa with HireBroker.',
    canonicalUrl: `${baseUrl}/hotels`
  },
  '/property-search-browse': {
    title: 'Find Properties for Sale & Rent',
    description: 'Search properties, apartments, houses, land, hostels, and hotels across Uganda and East Africa. Use powerful filters to find the right home or investment.',
    canonicalUrl: `${baseUrl}/property-search-browse`
  },
  '/login': {
    title: 'User Login',
    description: 'Sign in to your HireBroker user account to manage saved properties, searches, and notice-board posts.',
    canonicalUrl: `${baseUrl}/login`,
    robots: 'noindex,nofollow',
  },
  '/register': {
    title: 'User Registration',
    description: 'Create a user account to save favorites, post property needs, and get matched with verified listings across East Africa.',
    canonicalUrl: `${baseUrl}/register`,
    robots: 'noindex,nofollow',
  },
  '/pro/login': {
    title: 'Real Estate Agent Login',
    description: 'Sign in as a verified agent, broker, or property advisor on HireBroker.',
    canonicalUrl: `${baseUrl}/pro/login`,
    robots: 'noindex,nofollow',
  },
  '/pro/register': {
    title: 'Real Estate Agent Registration',
    description: 'Register as an agent, broker, or advisor. Complete verification to earn a trust badge and respond to user needs.',
    canonicalUrl: `${baseUrl}/pro/register`,
    robots: 'noindex,nofollow',
  },
  '/saved-properties': {
    title: 'Saved Properties',
    description: 'View your saved properties and latest listings in one place. Manage favorites, continue browsing, and compare properties easily.',
    canonicalUrl: `${baseUrl}/saved-properties`,
    robots: 'noindex,nofollow',
  },
  '/about': {
    title: 'About HireBroker',
    description: 'Learn how HireBroker is transforming real estate in East Africa with verified listings, expert brokers, and a better property search experience.',
    canonicalUrl: `${baseUrl}/about`
  },
  '/team': {
    title: 'Meet the HireBroker Team',
    description: 'Get to know the experienced team behind HireBroker, your trusted property brokers and real estate advisors in Uganda and the region.',
    canonicalUrl: `${baseUrl}/team`
  },
  '/careers': {
    title: 'Careers at HireBroker',
    description: 'Explore career opportunities at HireBroker and join a fast-growing real estate platform serving Uganda and East Africa.',
    canonicalUrl: `${baseUrl}/careers`
  },
  '/press': {
    title: 'Press & Media | HireBroker',
    description: 'Read HireBroker press releases, media coverage, and news about our property marketplace in Uganda and East Africa.',
    canonicalUrl: `${baseUrl}/press`
  },
  '/list-property': {
    title: 'List Your Property on HireBroker',
    description: 'Reach thousands of buyers and renters by listing your property on HireBroker. Sell, rent, or advertise land fast with expert support.',
    canonicalUrl: `${baseUrl}/list-property`
  },
  '/help': {
    title: 'HireBroker Help Center',
    description: 'Find answers to common questions, browse support resources, and get help using HireBroker’s property search tools and services.',
    canonicalUrl: `${baseUrl}/help`
  },
  '/how-fees-work': {
    title: 'How Rental Hunting Fees Work',
    description: 'HireBroker holds your rental brokerage fee, covers realtor transport from it, and pays the realtor only after you accept a property. HireBroker does not hold money for land transactions.',
    canonicalUrl: `${baseUrl}/how-fees-work`
  },
  '/contact': {
    title: 'Contact HireBroker',
    description: 'Contact HireBroker for property advice, landlord services, app support, and help finding rental homes, land, hostels, or hotels.',
    canonicalUrl: `${baseUrl}/contact`
  },
  '/user-dashboard': {
    title: 'My Dashboard',
    description: 'Manage your saved properties, search alerts, and bookings from your HireBroker dashboard.',
    canonicalUrl: `${baseUrl}/user-dashboard`,
    robots: 'noindex,nofollow',
  },
  '/user-profile': {
    title: 'User Profile',
    description: 'Keep your HireBroker profile up to date to get accurate property recommendations and broker support.',
    canonicalUrl: `${baseUrl}/user-profile`,
    robots: 'noindex,nofollow',
  },
  '/verification': {
    title: 'Real Estate Agent Verification',
    description: 'Verify your agent, broker, or advisor credentials to earn a HireBroker trust badge and build client confidence.',
    canonicalUrl: `${baseUrl}/verification`,
    robots: 'noindex,nofollow',
  },
  '/notice-board': {
    title: 'Property Needs Notice Board | HireBroker',
    description: 'Post what you need or respond as a verified real estate agent. HireBroker’s collaborative notice board matches seekers with agents, brokers, and advisors.',
    canonicalUrl: `${baseUrl}/notice-board`
  },
  '/workspace': {
    title: 'Agent Workspace',
    description: 'Collaborate and close deals with co-brokering, CRM, advisory bookings, and digital document signing on HireBroker.',
    canonicalUrl: `${baseUrl}/workspace`,
    robots: 'noindex,nofollow',
  },
  '/agent-dashboard': {
    title: 'Real Estate Agent Dashboard',
    description: 'Professional agent console to manage listings, CRM, co-brokering, bookings, and documents on HireBroker.',
    canonicalUrl: `${baseUrl}/agent-dashboard`,
    robots: 'noindex,nofollow',
  },
  '/privacy': {
    title: 'Privacy Policy',
    description: 'Read HireBroker’s privacy policy to understand how we collect, use, and protect your personal and property search data.',
    canonicalUrl: `${baseUrl}/privacy`
  },
  '/terms': {
    title: 'Terms & Conditions',
    description: 'Review the HireBroker terms and conditions for using our property marketplace, listings, and broker services.',
    canonicalUrl: `${baseUrl}/terms`
  },
  '/guides': {
    title: 'Real Estate Guides & Advice',
    description: 'Read expert guides on buying, renting, and investing in property across Uganda and East Africa.',
    canonicalUrl: `${baseUrl}/guides`
  },
  '/reports': {
    title: 'Market Reports & Insights',
    description: 'Access real estate market reports, investment research, and property trends for Uganda and East Africa.',
    canonicalUrl: `${baseUrl}/reports`
  },
  '/tips': {
    title: 'Property Tips & Investment Advice',
    description: 'Get property tips, rental advice, and investment strategies for buyers and renters across East Africa.',
    canonicalUrl: `${baseUrl}/tips`
  },
  '/blog': {
    title: 'HireBroker Blog',
    description: 'Read the latest real estate news, property market updates, and expert commentary from HireBroker.',
    canonicalUrl: `${baseUrl}/blog`
  },
  '*': {
    title: 'Page Not Found',
    description: 'The page you are looking for could not be found. Return to HireBroker to continue searching property listings and broker services.',
    canonicalUrl: `${baseUrl}`,
    robots: 'noindex,nofollow',
  }
};

const SeoLayout = ({ seo, children }) => (
  <>
    <SEO {...seo} />
    {children}
  </>
);

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <FloatingActionButton />
        <AiAssistantChatbot />
        <RouterRoutes>
          {/* Define your route here */}
          <Route path="/" element={<SeoLayout seo={seoConfig['/']}><Home /></SeoLayout>} />
          <Route path="/hostels" element={<SeoLayout seo={seoConfig['/hostels']}><Hostels /></SeoLayout>} />
          <Route path="/hotels" element={<SeoLayout seo={seoConfig['/hotels']}><Hotels /></SeoLayout>} />
          <Route path="/property-search-browse" element={<SeoLayout seo={seoConfig['/property-search-browse']}><PropertySearchBrowse /></SeoLayout>} />
          <Route path="/login" element={<SeoLayout seo={seoConfig['/login']}><Login /></SeoLayout>} />
          <Route path="/register" element={<SeoLayout seo={seoConfig['/register']}><Register /></SeoLayout>} />
          <Route path="/pro/login" element={<SeoLayout seo={seoConfig['/pro/login']}><ProLogin /></SeoLayout>} />
          <Route path="/pro/register" element={<SeoLayout seo={seoConfig['/pro/register']}><ProRegister /></SeoLayout>} />
          <Route element={<ProtectedRoute />}>
            <Route path="/user-dashboard" element={<SeoLayout seo={seoConfig['/user-dashboard']}><UserDashboard /></SeoLayout>} />
            <Route path="/user-dashboard/profile" element={<SeoLayout seo={seoConfig['/user-profile']}><UserProfile /></SeoLayout>} />
            <Route path="/user-profile" element={<SeoLayout seo={seoConfig['/user-profile']}><UserProfile /></SeoLayout>} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/verification" element={<SeoLayout seo={seoConfig['/verification']}><VerificationPage /></SeoLayout>} />
            <Route path="/agent-dashboard" element={<SeoLayout seo={seoConfig['/agent-dashboard']}><AgentDashboard /></SeoLayout>} />
            <Route path="/agent-dashboard/properties" element={<AgentProperties />} />
            <Route path="/agent-dashboard/properties/new" element={<AgentPropertyNew />} />
            <Route path="/agent-dashboard/notifications" element={<NotificationsPage />} />
            <Route path="/agent-dashboard/profile" element={<UserProfile />} />
            <Route path="/agent-dashboard/verification" element={<VerificationPage />} />
            <Route path="/agent-dashboard/subscription" element={<AgentSubscription />} />
            <Route path="/agent-dashboard/promote" element={<AgentPromote />} />
            <Route path="/workspace" element={<SeoLayout seo={seoConfig['/workspace']}><WorkspaceHome /></SeoLayout>} />
            <Route path="/workspace/co-broker" element={<CoBrokerPage />} />
            <Route path="/workspace/crm" element={<CrmPage />} />
            <Route path="/workspace/bookings" element={<BookingsPage />} />
            <Route path="/workspace/documents" element={<DocumentsPage />} />
          </Route>
          <Route path="/notice-board" element={<SeoLayout seo={seoConfig['/notice-board']}><NoticeBoardPage /></SeoLayout>} />
          <Route path="/notice-board/:id" element={<NoticeBoardDetail />} />
          <Route path="/property/:slug_id" element={<PropertyDetail />} />
          <Route path="/agent/:id" element={<AgentProfile />} />
          <Route path="/hostels/:slug_id" element={<HostelDetail />} />
          <Route path="/hotels/:slug_id" element={<HotelDetail />} />
          <Route path="/for-rent" element={<MarketPage intent="for-rent" />} />
          <Route path="/for-rent/:city" element={<MarketPage intent="for-rent" />} />
          <Route path="/for-sale" element={<MarketPage intent="for-sale" />} />
          <Route path="/for-sale/:city" element={<MarketPage intent="for-sale" />} />
          <Route path="/land" element={<MarketPage intent="land" />} />
          <Route path="/land/:city" element={<MarketPage intent="land" />} />
          <Route path="/sitemap" element={<HtmlSitemap />} />
          <Route path="/saved-properties" element={<SeoLayout seo={seoConfig['/saved-properties']}><SavedPropertiesPage /></SeoLayout>} />

          {/* Company Pages */}
          <Route path="/about" element={<SeoLayout seo={seoConfig['/about']}><AboutUs /></SeoLayout>} />
          <Route path="/team" element={<SeoLayout seo={seoConfig['/team']}><OurTeam /></SeoLayout>} />
          <Route path="/careers" element={<SeoLayout seo={seoConfig['/careers']}><Careers /></SeoLayout>} />
          <Route path="/press" element={<SeoLayout seo={seoConfig['/press']}><Press /></SeoLayout>} />

          {/* Services Pages */}
          <Route path="/list-property" element={<SeoLayout seo={seoConfig['/list-property']}><ListProperty /></SeoLayout>} />

          {/* Support Pages */}
          <Route path="/help" element={<SeoLayout seo={seoConfig['/help']}><HelpCenter /></SeoLayout>} />
          <Route path="/how-fees-work" element={<SeoLayout seo={seoConfig['/how-fees-work']}><HowFeesWork /></SeoLayout>} />
          <Route path="/contact" element={<SeoLayout seo={seoConfig['/contact']}><ContactUs /></SeoLayout>} />
          <Route path="/privacy" element={<SeoLayout seo={seoConfig['/privacy']}><PrivacyPolicy /></SeoLayout>} />
          <Route path="/terms" element={<SeoLayout seo={seoConfig['/terms']}><TermsOfService /></SeoLayout>} />

          {/* Resources Pages */}
          <Route path="/guides" element={<SeoLayout seo={seoConfig['/guides']}><PropertyGuides /></SeoLayout>} />
          <Route path="/reports" element={<SeoLayout seo={seoConfig['/reports']}><MarketReports /></SeoLayout>} />
          <Route path="/tips" element={<SeoLayout seo={seoConfig['/tips']}><InvestmentTips /></SeoLayout>} />
          <Route path="/blog" element={<SeoLayout seo={seoConfig['/blog']}><Blog /></SeoLayout>} />

          <Route path="*" element={<SeoLayout seo={seoConfig['*']}><NotFound /></SeoLayout>} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
